import { Box, Typography } from "@mui/material";
import { alpha, type Theme } from "@mui/material/styles";
import type { ReactNode } from "react";

import { ApiError } from "../../../lib/api.js";
import type { SxStyle } from "../../sales/leads/heatStyles.js";

/** Tone → border + soft surface fill for the empty/error panel. */
function toneSx(variant: "info" | "warn" | "bad"): SxStyle {
  switch (variant) {
    case "warn":
      return {
        borderColor: (t: Theme) => alpha(t.palette.warning.main, 0.4),
        bgcolor: (t: Theme) => alpha(t.palette.warning.main, 0.05),
      };
    case "bad":
      return {
        borderColor: (t: Theme) => alpha(t.palette.error.main, 0.4),
        bgcolor: (t: Theme) => alpha(t.palette.error.main, 0.05),
      };
    case "info":
    default:
      return { borderColor: "divider", bgcolor: "surfaceSoft" };
  }
}

/**
 * Friendly empty / error state used everywhere inside `/support/*`. The
 * dashboard's support proxy collapses every upstream failure into a
 * typed `code` string — `SUPPORT_CENTER_NOT_CONFIGURED`,
 * `SUPPORT_CENTER_UNREACHABLE`, `SUPPORT_CENTER_UPSTREAM_ERROR`. We map
 * each into specific guidance instead of dumping a stack trace.
 */
export function SupportEmptyState({
  title,
  description,
  action,
  variant = "info",
}: {
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  variant?: "info" | "warn" | "bad";
}): JSX.Element {
  return (
    <Box
      sx={[
        {
          borderRadius: 1,
          border: 1,
          p: 4,
          textAlign: "center",
          color: "text.primary",
        },
        toneSx(variant),
      ]}
    >
      <Typography sx={{ fontSize: "1rem", fontWeight: 600, letterSpacing: "-0.01em" }}>
        {title}
      </Typography>
      {description && (
        <Typography
          sx={{ mx: "auto", mt: 1, maxWidth: "36rem", fontSize: 14, lineHeight: 1.6, color: "text.secondary" }}
        >
          {description}
        </Typography>
      )}
      {action && (
        <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>{action}</Box>
      )}
    </Box>
  );
}

/**
 * Convenience wrapper: take whatever `useQuery.error` we got and render
 * the right empty state for it. Anything we don't recognise falls
 * through to a generic message with the original error text. Upstream
 * diagnostics (status, URL, body excerpt) are stitched into a small
 * details block at the bottom so staff can copy/paste into Slack.
 */
export function SupportErrorState({
  error,
  resourceLabel = "data",
}: {
  error: unknown;
  resourceLabel?: string;
}): JSX.Element {
  if (error instanceof ApiError) {
    if (error.code === "SUPPORT_CENTER_NOT_CONFIGURED") {
      return (
        <SupportEmptyState
          variant="warn"
          title="Support Center URL is not configured"
          description="Set SUPPORT_CENTER_API_URL in the dashboard server's .env, then restart the server. In dev that's typically http://localhost:3000."
        />
      );
    }
    if (error.code === "SUPPORT_CENTER_UNREACHABLE") {
      return (
        <SupportEmptyState
          variant="warn"
          title="Support Center is unreachable"
          description="The Rails support-center isn't responding. Confirm it's running and that the dashboard server can reach SUPPORT_CENTER_API_URL on the network."
          action={<UpstreamDetails details={error.details} />}
        />
      );
    }
    if (error.code === "SUPPORT_CENTER_UPSTREAM_ERROR") {
      const details = error.details as
        | { upstream_status?: number; upstream_body?: unknown; url?: string }
        | null;
      // The dashboard server's proxy mirrors the upstream HTTP status
      // onto the response itself (`reply.code(res.statusCode)`), so the
      // ApiError's own `status` field is a reliable fallback when the
      // body's `details` envelope is missing — e.g. when the dashboard
      // server is running an old build that returns the flat-fields
      // shape instead of nesting under `details`. Treat zero as "no
      // status" since it isn't a real HTTP code (some browser stacks
      // surface 0 for connection-level failures).
      const fromDetails = details?.upstream_status;
      const fromError = error.status > 0 ? error.status : undefined;
      const upstream = fromDetails ?? fromError;
      const isMissingEndpoint = upstream === 404;
      const isAuthError = upstream === 401 || upstream === 403;
      const isServerError = typeof upstream === "number" && upstream >= 500;
      return (
        <SupportEmptyState
          variant={isMissingEndpoint ? "info" : "bad"}
          title={
            isMissingEndpoint
              ? `This ${resourceLabel} endpoint isn't available yet`
              : isAuthError
                ? "Backend rejected the dashboard's auth"
                : `Could not load ${resourceLabel}`
          }
          description={
            isMissingEndpoint
              ? "The Rails backend hasn't shipped this endpoint yet. The dashboard UI is ready — wire up the Rails route and this page lights up."
              : isAuthError
                ? "The dashboard's HMAC + internal-service JWT didn't pass Rails' verifier. Confirm SUPPORT_CENTER_JWT_SECRET and DASHBOARD_TO_SUPPORT_HMAC_SECRET on the dashboard match the Rails app's values."
                : isServerError
                  ? `The Support Center API returned HTTP ${upstream}. Check the Rails logs for the underlying error (DB pool, exception, missing migration).`
                  : typeof upstream === "number"
                    ? `The Support Center API returned HTTP ${upstream}. ${error.message}`
                    : `The Support Center API returned an error (${error.message}). Confirm the dashboard server is running and able to reach the Rails app, then retry.`
          }
          action={<UpstreamDetails details={error.details} />}
        />
      );
    }
  }
  return (
    <SupportEmptyState
      variant="bad"
      title={`Could not load ${resourceLabel}`}
      description={error instanceof Error ? error.message : "Unknown error"}
    />
  );
}

/**
 * Inline `<details>` showing the raw upstream URL, status, and body
 * snippet from the dashboard server's typed error envelope. Collapsed
 * by default — power users open it; everyone else reads the
 * description. Defensive about missing fields so it works for both the
 * UPSTREAM_ERROR shape and the UNREACHABLE shape.
 */
function UpstreamDetails({ details }: { details: unknown }): JSX.Element | null {
  if (!details || typeof details !== "object") return null;
  const d = details as {
    upstream_status?: number;
    upstream_body?: unknown;
    url?: string;
    reason?: string;
  };
  if (!d.upstream_status && !d.url && !d.reason) return null;
  return (
    <Box
      component="details"
      sx={{
        mt: 1,
        maxWidth: "36rem",
        borderRadius: 1,
        border: 1,
        borderColor: "divider",
        bgcolor: "surfaceSoft",
        p: 1,
        textAlign: "left",
        fontSize: 12,
      }}
    >
      <Box component="summary" sx={{ cursor: "pointer", fontWeight: 500, color: "text.disabled" }}>
        Diagnostic details
      </Box>
      <Box component="dl" sx={{ mt: 1, display: "grid", gap: 0.5 }}>
        {d.url && (
          <DiagRow label="URL">
            <Box component="code" sx={{ wordBreak: "break-all" }}>{d.url}</Box>
          </DiagRow>
        )}
        {typeof d.upstream_status === "number" && (
          <DiagRow label="HTTP status">
            <code>{d.upstream_status}</code>
          </DiagRow>
        )}
        {d.reason && (
          <DiagRow label="Reason">
            <Box component="code" sx={{ wordBreak: "break-all" }}>{d.reason}</Box>
          </DiagRow>
        )}
        {d.upstream_body !== undefined && (
          <DiagRow label="Response">
            <Box
              component="pre"
              sx={{ maxHeight: 160, overflow: "auto", borderRadius: 0.5, bgcolor: "action.hover", p: 1, fontSize: 10 }}
            >
              {typeof d.upstream_body === "string"
                ? d.upstream_body
                : JSON.stringify(d.upstream_body, null, 2)}
            </Box>
          </DiagRow>
        )}
      </Box>
    </Box>
  );
}

function DiagRow({ label, children }: { label: string; children: ReactNode }): JSX.Element {
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "100px 1fr", gap: 1 }}>
      <Box component="dt" sx={{ color: "text.disabled" }}>{label}</Box>
      <Box component="dd" sx={{ m: 0 }}>{children}</Box>
    </Box>
  );
}
